type DateInput = string | Date;

// Is current environment development?
export const isDev = import.meta.env.DEV;

export const convertDate = (
  date?: DateInput,
  options?: Intl.DateTimeFormatOptions
) => {
  if (!date) return "";

  const refinedOptions = options ?? {};
  const resolvedDate = date instanceof Date ? date : new Date(date);
  // refinedOptions = {...refinedOptions, timeZone: "utc"};

  return resolvedDate.toLocaleDateString("cs-CZ", refinedOptions);
};

export const convertDateTime = (
  date?: DateInput,
  options?: Intl.DateTimeFormatOptions
) => {
  if (!date) return "";

  const refinedOptions = options ?? { timeStyle: 'short', dateStyle: 'medium' };
  const resolvedDate = date instanceof Date ? date : new Date(date);
  // refinedOptions = {...refinedOptions, timeZone: "utc"};

  return resolvedDate.toLocaleString("cs-CZ", refinedOptions);
};

// Ascending sort of an array with objects with property createdAt, used for documents, notes etc..
type ElementWithDate = Record<string, string | number | Date | null | undefined>;

type SortArrayOptions = {
  dateProperty?: string;
  order?: "ASC" | "DESC";
};

export const sortArrayWithDates = (
  arrayWithDates?: ElementWithDate[],
  options: SortArrayOptions = { dateProperty: "createdAt", order: "ASC" }
) => {
  if (!arrayWithDates) return [];
  const copiedArray = arrayWithDates.slice();
  return copiedArray.sort((a, b) => {
    const dateProperty = options.dateProperty ?? "createdAt";
    const order = options.order ?? "ASC";
    const firstDate = new Date(a[dateProperty]).getTime();
    const secondDate = new Date(b[dateProperty]).getTime();
    if (order === "ASC") return firstDate - secondDate;
    else return secondDate - firstDate;
  });
};

export const removeEmpty = (obj: Record<string, unknown>, removeEmptyStrings: boolean = false) => Object.fromEntries(Object.entries(obj).filter(([, v]) => (v != null && (removeEmptyStrings ? v !== "" : true))));

export function formatPrice(price: number): string {
  return new Intl.NumberFormat("cs-CZ", {
    style: "currency",
    currency: "CZK",
    maximumFractionDigits: 2,
  }).format(price);
}

export const TOAST_MESSAGES = {
  INTERNAL_SERVER_ERROR:
    "Nastala chyba na serveru. Zkuste to prosím znovu později.",

  // Successful Auth
  SUCCESSFUL_LOGIN: "Úspěšně jste se přihlásili.",
  SUCCESSFUL_REGISTER: "Úspěšně jste se zaregistrovali.",
  SUCCESSFUL_LOGOUT: "Úspěšně jste se odhlásili.",
  SUCCESSFUL_RESET_PASSWORD: "Úspěšně jste si změnili heslo.",
  SUCCESSFUL_RESET_PASSWORD_SEND_EMAIL:
    "Zaslali jsme Vám do emailu odkaz pro resetování hesla.",
  SUCCESSFUL_CHANGE_PASSWORD:
    "Úspěšně jste si změnili heslo. \nProsím přihlašte se znovu pod novým heslem.",
  VERIFICATION_EMAIL_RESENT: "Ověřovací email byl odeslán.",
  EMAIL_SUCCESSFULLY_VERIFIED: "Váš email byl úspěšně ověřen.",

  // Failed Auth
  FAILED_LOGIN: "Nezdařilo se Vás přihlásit.",
  FAILED_REGISTER: "Nezdařilo se Vás zaregistrovat.",
  FAILED_LOGOUT: "Nezdařilo se Vás odhlásit.",
  EMAIL_VERIFICATION_FAILED:
    "Nezdařilo se ověřit Váš email. Zkuste to prosím znovu.",
  USER_ACCOUNT_CANCELED: "Váš účet byl zrušen. Pokud se domníváte, že se jedná o chybu, kontaktujte nás.",
  UNVERIFIED_USER: "Nezdařilo se Vás ověřit.",
  USER_NOT_CLIENT: "Váš účet není zákaznického typu.",
  USER_NOT_ADMIN: "Váš účet není administrátorského typu.",
  INSUFFICIENT_ACCOUNT_TYPE: "Váš účet není požadovaného typu.",
  INVALID_USER: "Nezdařilo se Vás ověřit.",
  FORBIDDEN: "Na tuto stránku nebo akci nemáte oprávnění.",
  USER_ALREADY_EXISTS: "Uživatel se stejným emailem nebo IČO již existuje.",
  WRONG_EMAIL_PASSWORD_COMBINATION: "Email nebo heslo není správné.",
  TOKEN_NOT_VALID: "Váš token pro resetování hesla není platný.",
  FAILED_RESET_PASSWORD: "Nezdařilo se resetovat Vaše heslo.",
  FAILED_RESET_PASSWORD_SEND_EMAIL:
    "Nezdařilo se zaslat odkaz pro resetování hesla na Váš email.",
  NEW_PASSWORDS_DO_NOT_MATCH: "Nová hesla se neshodují.",
  FAILED_CHANGE_PASSWORD: "Nezdařilo se změnit Vaše heslo.",
  PASSWORD_INCORRECT: "Zadané heslo není správné.",
  PASSWORD_VERIFICATION_LIMIT:
    "E-mail pro reset hesla Vám byl již zaslán. Pokud nepřijde, zkontrolujte složku spam, případně to zkuste znovu za 5 minut.",
  VERIFICATION_EMAIL_LIMIT:
    "E-mail pro ověření Vám byl již zaslán. Pokud nepřijde, zkontrolujte složku spam, případně to zkuste znovu za 1 minutu.",
  COLLECTION_POINT_NOT_FOUND: "Zadané odběrné místo nebylo nalezeno.",
  BUILDING_NOT_FOUND: "Zadaný objekt nebyl nalezen.",
  COLLECTION_POINT_ALREADY_EXISTS: "Odběrné místo s daným identifikátorem již existuje.",
  COLLECTION_POINT_IDENTIFIER_AMBIGUOUS: "K danému identifikátoru existuje více odběrných míst. Vyberte prosím správné odběrné místo ručně.",
  METER_CODE_ALREADY_ASSIGNED: "Číslo měřidla už je přiřazeno k jinému odběrnému místu v této obci.",
  METER_CODE_REMOVAL_NOT_ALLOWED: "Původně uložené číslo měřidla nelze při úpravě spotřeby odstranit.",
  METER_CHANGE_DETAILS_REQUIRED: "Při změně měřidla doplňte konečný stav původního měřidla a počáteční stav nového měřidla.",
  INVALID_ICO: "Zadané IČO není platné.",
  EMAIL_ALREADY_REGISTERED: "Zadaný email je již registrován. Zkuste se prosím přihlásit.",
  MUNICIPALITY_NOT_FOUND: "Zadaná obec nebyla nalezena.",
  CANNOT_DELETE_PRIMARY_USER: "Primárního uživatele nelze smazat.",
  USER_NOT_FOUND: "Zadaný uživatel nebyl nalezen.",
};

export const getAlertMessage = (
  toastMessageKey: keyof typeof TOAST_MESSAGES,
  backupMessage?: string
): string => {
  if (toastMessageKey in TOAST_MESSAGES)
    return TOAST_MESSAGES[toastMessageKey];
  if (backupMessage) return backupMessage;

  return "Neznámá chyba.";
};

export const numberOrUndefToNumber = (
  value: string | undefined | number
): number => {
  if (typeof value === "number") return value;
  return value ? parseFloat(value) : 0;
};

export const formatConsumption = (consumption: number | null | undefined, unit = 'kWh'): string => {
  if (consumption === undefined || consumption === null || isNaN(consumption)) {
    return "-";
  }
  const formatted = consumption.toLocaleString("cs-CZ", {
    maximumFractionDigits: 2,
  });

  return unit ? `${formatted} ${unit}` : formatted;
};

export const addTax = (priceWithoutTax: number, taxRatePercent: number): string => {
  return (priceWithoutTax * (1 + taxRatePercent / 100)).toFixed(2);
};

export const removeTax = (priceWithTax: number, taxRatePercent: number): string => {
  return (priceWithTax / (1 + taxRatePercent / 100)).toFixed(2);
};

function handleZero(inp: string | number): string {
  return ('0' + inp).slice(-2);
}

export function dateToDB(date: Date | string): string {
  if (typeof date === 'string') date = new Date(date);
  return `${date.getFullYear()}-${handleZero(date.getMonth() + 1)}-${handleZero(date.getDate())}`;
}
